import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { SvgIconComponent } from "../../components/svg-icon/svg-icon.component";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
import { GateStatus, GateStatusObservation } from "../../../models/shared/gateStatus";
import { GATE_STATUS } from "../../../enums/gate-status.enum";

enum GATE_ANIMATION_MODE {
  LIVE = "live",
  REPLAY = "replay",
}

interface GateAnimation {
  id: number;
  mode: GATE_ANIMATION_MODE;
  direction: `${GATE_STATUS.OPENING | GATE_STATUS.CLOSING}`;
}
// The existing opening SVG includes a 250 ms delay followed by a 1.71 s animation.
const REPLAY_DURATION_MS = 2000;

@Component({
  selector: "app-gate-position",
  standalone: true,
  imports: [SvgIconComponent, TranslateModule, NgTemplateOutlet, TimeoutComponent, ProgressSpinnerModule],
  templateUrl: "./gate-position.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./gate-position.component.scss"],
})
export class GatePositionComponent {
  public readonly gateStatus = input<GateStatusObservation | null | undefined>(null);
  public readonly timeout = input(false);
  public readonly compact = input(false);
  public readonly lastActionTimestamp = input<string>();

  protected readonly loading = computed(() => this.gateStatus() === null);
  protected readonly gateStates = { ...GATE_STATUS, UNKNOWN: "unknown" } as const;
  protected readonly animationModes = GATE_ANIMATION_MODE;
  protected readonly animation = signal<GateAnimation | null>(null);
  protected readonly iconStatus = computed(
    () => this.animation()?.direction ?? this.gateStatus()?.value ?? this.gateStates.UNKNOWN
  );

  private previousObservation?: GateStatusObservation;
  private nextAnimationId = 0;
  private replayTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    effect(() => {
      const observation = this.gateStatus();
      const timeout = this.timeout();
      untracked(() => this.updateAnimation(observation, timeout));
    });
    inject(DestroyRef).onDestroy(() => this.stopAnimation());
  }

  private updateAnimation(observation: GateStatusObservation | null | undefined, timeout: boolean): void {
    const previous = this.previousObservation;

    if (!observation || timeout) {
      this.previousObservation = undefined;
      this.stopAnimation();
      return;
    }
    this.previousObservation = observation;
    const sameGate = previous?.gateSystemId === observation.gateSystemId;
    const renderedStatus = previous?.value;
    // Signals may coalesce multiple observations before the next render.
    const previousValue = observation.previousValue ?? renderedStatus;
    if (
      observation.delivery === "update" &&
      sameGate &&
      renderedStatus === observation.value &&
      previousValue === observation.value
    )
      return;

    this.stopAnimation();
    if (observation.value === GATE_STATUS.OPENING || observation.value === GATE_STATUS.CLOSING) {
      this.animation.set({ id: ++this.nextAnimationId, mode: GATE_ANIMATION_MODE.LIVE, direction: observation.value });
      return;
    }
    if (observation.delivery === "snapshot" || !sameGate) return;

    const direction = this.getReplayDirection(previousValue, observation.value);
    if (direction) {
      this.animation.set({ id: ++this.nextAnimationId, mode: GATE_ANIMATION_MODE.REPLAY, direction });
      this.replayTimer = setTimeout(() => this.animation.set(null), REPLAY_DURATION_MS);
    }
  }

  private getReplayDirection(
    previous: GateStatus | undefined,
    current: GateStatus
  ): GateAnimation["direction"] | undefined {
    const positions: Partial<Record<GateStatus, number>> = {
      [GATE_STATUS.CLOSE]: 0,
      [GATE_STATUS.PARTIAL]: 1,
      [GATE_STATUS.OPEN]: 2,
    };
    const from = previous === undefined ? undefined : positions[previous];
    const to = positions[current];
    if (from === undefined || to === undefined || from === to) return undefined;
    return to > from ? GATE_STATUS.OPENING : GATE_STATUS.CLOSING;
  }

  private stopAnimation(): void {
    clearTimeout(this.replayTimer);
    this.replayTimer = undefined;
    this.animation.set(null);
  }
}
